/**
 * FALLBACK BASEMAP — a rough, always-available land / water / coastline base so the
 * user is never left staring at a blank background where no real chart covers the
 * view. The vector ENC cells and the raster charts only cover the areas they were
 * cut for; sail off the edge of the RYA training cells (or open an area with no
 * pack) and, with satellite off, there is currently nothing under the empty map but
 * the flat background colour. This lays a low-detail world land/water picture right
 * at the BOTTOM of the stack, so wherever a real chart exists its opaque depth-area
 * and land fills cover this completely, and wherever none does the user still sees
 * rough coastline for context.
 *
 * OFFLINE FIRST. The base coastline now comes from a BUNDLED world land dataset
 * (public/basemap/ne_110m_land.geojson — Natural Earth 1:110m land, simplified and
 * rounded to ~2 dp so it ships at ~80 KB). Because it is bundled it needs no network
 * at all: a water background with the land polygons painted on top gives land, sea
 * and a coast edge with zero connection, which is the whole point out at sea with no
 * signal. On TOP of that bundled land, when we DO have a connection, we still lay the
 * MapTiler OpenMapTiles vector `water` layer (the same source map/vectorBasemap.ts
 * streams). Its finer 1:? coastline sharpens the coarse 110m coast where the network
 * is there, but it sits ABOVE the bundled land and never replaces it, so losing
 * signal just drops back to the bundled coast rather than to a blank view. Both stay
 * BELOW every real chart, so a covered area hides all of this.
 *
 * The two datasets disagree about where the coast is, and the finer one has no land
 * polygons of its own, so once its tiles arrive we paint a land-colour background
 * under the water fill and let the water paint the sea back on top (addTilesLand).
 * Without that, land the coarse 110m polygon doesn't reach shows through as water.
 *
 * No roads, labels or glyphs on either layer: this is a rough locator base, kept
 * deliberately plain so it never competes with a real chart and needs no glyph
 * endpoint (so no setGlyphs, no blank-screen risk).
 *
 * Ordering: every layer id is prefixed `fbbase-` and the two sources are
 * `fbbase-land-src` (bundled geojson) and `fbbase-src` (MapTiler vector). The group
 * inserts beneath the vector cells, raster charts and every app overlay, and also
 * beneath the `mtbase-*` OpenSeaMap base if that happens to be present, so this never
 * obscures anything real. setFallbackBasemap re-seats the group under the current
 * content on every re-apply, the same raise/re-seat contract vectorBasemap follows,
 * so a provider swap that adds layers above it can't leave it on top.
 */
import type { Map as MaplibreMap, LayerSpecification, MapSourceDataEvent } from 'maplibre-gl';
import {
  maptilerVectorTiles,
  MAPTILER_VECTOR_MAXZOOM,
  MAPTILER_ATTRIBUTION,
} from '../chart-sources/maptilerConfig';

/** MapTiler vector source (optional finer online coast). */
const SRC = 'fbbase-src';
/** Bundled world-land geojson source (the always-available offline coast). */
const LAND_SRC = 'fbbase-land-src';
const PREFIX = 'fbbase-';

/**
 * The bundled land asset, resolved against the app's base URL exactly like the NOAA
 * manifest (chart-sources/noaaManifest.ts) so it works under any deploy sub-path. It
 * lives in public/ so the build copies it verbatim into the bundle.
 */
export function landDataUrl(): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return `${base}basemap/ne_110m_land.geojson`;
}

/**
 * A quiet locator palette. Land and water match the OpenSeaMap vector base so the
 * fallback reads as the same family of map, and the coast line is a muted version of
 * the water so the coastline edge is legible without shouting.
 */
const C = {
  land: '#f4f1ea',
  water: '#bcd7e6',
  coast: '#8fa9b6',
};

/** Credit for the bundled coastline. Natural Earth is public domain; we credit it anyway. */
const LAND_ATTRIBUTION = 'Land: Natural Earth';

export interface ActiveFallbackBasemap {
  /** Vector tile URL template (…/{z}/{x}/{y}.pbf?key=…). */
  url: string;
  /** Source max native zoom (OpenMapTiles v3 = 14); MapLibre overzooms beyond. */
  maxzoom: number;
  /** MapTiler + OSM credit for the base data. */
  attribution: string;
}

/**
 * The active fallback base built from the app's existing MapTiler config, so wiring
 * it in is a single call with no per-call-site config. Kept as a factory (not a
 * const) to match maptilerVectorTiles() being a function. The bundled land underneath
 * is intrinsic to the module and needs no config, so it is not part of this object.
 */
export function maptilerFallbackBasemap(): ActiveFallbackBasemap {
  return {
    url: maptilerVectorTiles(),
    maxzoom: MAPTILER_VECTOR_MAXZOOM,
    attribution: MAPTILER_ATTRIBUTION,
  };
}

/** Every layer id this module can add, bottom-to-top, so removeAll is exhaustive. */
const ALL_LAYER_IDS = [
  `${PREFIX}water`, // background: the sea everywhere (offline)
  `${PREFIX}land`, // bundled land polygons on top of the sea (offline)
  `${PREFIX}coast`, // coast edge from the bundled land outline (offline)
  `${PREFIX}tiles-land`, // land everywhere, painted only once the tiles arrive (online only)
  `${PREFIX}tiles-water`, // finer MapTiler sea, sharpening the coast (online only)
  `${PREFIX}tiles-coast`, // finer MapTiler coast hairline (online only)
] as const;

/**
 * The land / water / coastline layers, bottom-to-top.
 *
 * The bottom three are the OFFLINE base and are always present: a water-colour
 * background, the bundled land polygons over it, and a faint outline on those
 * polygons for the coast. When `withTiles` is true we add the two MapTiler vector
 * layers on top: a `water` fill that repaints the sea at finer detail (carving the
 * coarse 110m land back to the real coast) and a hairline on those same water
 * polygons. Offline the tile layers simply have no data and the bundled coast shows
 * through, which is exactly the fallback we want.
 */
function layerSpecs(withTiles: boolean): LayerSpecification[] {
  const specs: LayerSpecification[] = [
    { id: `${PREFIX}water`, type: 'background', paint: { 'background-color': C.water } },
    {
      id: `${PREFIX}land`,
      type: 'fill',
      source: LAND_SRC,
      paint: { 'fill-color': C.land },
    },
    {
      id: `${PREFIX}coast`,
      type: 'line',
      source: LAND_SRC,
      paint: {
        'line-color': C.coast,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.4, 12, 1.2],
        'line-opacity': 0.8,
      },
    },
  ];
  if (!withTiles) return specs;
  return [
    ...specs,
    {
      id: `${PREFIX}tiles-water`,
      type: 'fill',
      source: SRC,
      'source-layer': 'water',
      paint: { 'fill-color': C.water },
    },
    // A faint line on the same water polygons sharpens the coastline edge (a fill has
    // no stroke of its own), thicker as we zoom in so it stays a hairline, not a slab.
    {
      id: `${PREFIX}tiles-coast`,
      type: 'line',
      source: SRC,
      'source-layer': 'water',
      paint: {
        'line-color': C.coast,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.4, 12, 1.2],
        'line-opacity': 0.8,
      },
    },
  ];
}

/**
 * The id of the first NON-base layer, so the whole fallback group inserts beneath the
 * real charts and app overlays. Base = the map background, any existing `fbbase-*`
 * layers, and the OpenSeaMap `mtbase-*` base (the fallback must sit under that too);
 * everything else is content that must stay on top. Returns undefined when only base
 * layers exist (append to top, still above the background).
 */
export function firstContentLayerId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (l.id === 'background') continue;
    if (l.id.startsWith(PREFIX)) continue;
    if (l.id.startsWith('mtbase-')) continue;
    return l.id;
  }
  return undefined;
}

/** The land-colour background laid under the finer water fill, once tiles arrive. */
const TILES_LAND = `${PREFIX}tiles-land`;

/**
 * Paint land where the FINER data says land.
 *
 * The bundled Natural Earth coast is 1:110m, which is a blob at anything closer than
 * an ocean view, and the MapTiler water fill on top of it is far finer. Where the two
 * disagree (which at harbour zoom is most of a coastline) real land fell in the gap:
 * the coarse polygon didn't reach it and the fine water didn't cover it, so it came
 * out the water-colour background with a coastline drawn over it. That is the
 * half-transparent land Edd saw off Mull.
 *
 * OpenMapTiles carries water polygons but no land polygons, so the fix is the way
 * every OSM-style map does it: paint the whole view land colour and let the water
 * layer paint the sea back on top. The catch is that a background layer paints
 * whether or not there is data behind it, so adding this blind would show the whole
 * sea as land on an offline start. We therefore only lay it down once the vector
 * source has actually delivered a tile. Once it has, MapLibre keeps the low-zoom
 * parent tiles, so the sea carries on painting from those if the signal drops.
 */
function addTilesLand(map: MaplibreMap): void {
  if (map.getLayer(TILES_LAND)) return;
  // Only meaningful beneath the fine water fill; without it we would paint land
  // over the whole view and never paint the sea back.
  if (!map.getLayer(`${PREFIX}tiles-water`)) return;
  map.addLayer(
    { id: TILES_LAND, type: 'background', paint: { 'background-color': C.land } },
    `${PREFIX}tiles-water`,
  );
}

/** Detach the tile watcher, if one is attached. */
let unwatchTiles: (() => void) | null = null;

/**
 * Watch the vector source and add the land background the moment a tile lands.
 * Left attached (rather than one-shot) so the layer comes back if the group is
 * rebuilt while the source stays; removeAll detaches it.
 */
function watchTiles(map: MaplibreMap): void {
  if (typeof map.on !== 'function') return;
  const onData = (e: MapSourceDataEvent) => {
    if (e.sourceId !== SRC || !e.tile) return;
    addTilesLand(map);
  };
  map.on('sourcedata', onData);
  unwatchTiles = () => {
    if (typeof map.off === 'function') map.off('sourcedata', onData);
  };
}

let currentSig: string | null = null;

/** Remove every fbbase layer and both sources. */
function removeAll(map: MaplibreMap): void {
  if (unwatchTiles) {
    unwatchTiles();
    unwatchTiles = null;
  }
  for (const id of ALL_LAYER_IDS) if (map.getLayer(id)) map.removeLayer(id);
  if (map.getSource(SRC)) map.removeSource(SRC);
  if (map.getSource(LAND_SRC)) map.removeSource(LAND_SRC);
}

/**
 * Show the fallback basemap (replacing any current one), or clear it with null.
 *
 * The bundled land base is laid down every time, with no network needed. When `base`
 * carries a MapTiler url we add the finer vector layers on top of it, still beneath
 * the real charts. Idempotent: if the same configuration is already up, this only
 * re-seats the group under the current content rather than refetching, so it can be
 * called freely on every chart-source re-apply.
 */
export function setFallbackBasemap(map: MaplibreMap, base: ActiveFallbackBasemap | null): void {
  if (!base) {
    removeAll(map);
    currentSig = null;
    return;
  }
  const withTiles = Boolean(base.url);
  const sig = `${base.url}|${base.maxzoom}`;
  const specs = layerSpecs(withTiles);
  // Same configuration already up: nothing to refetch, just re-seat the group beneath
  // the current content (a provider swap may have added layers above it).
  if (currentSig === sig && map.getSource(LAND_SRC)) {
    const before = firstContentLayerId(map);
    // Walk the full ordered id list, not the add-specs: tiles-land is added later
    // (when the tiles arrive) and must be re-seated in its place with the rest.
    for (const id of ALL_LAYER_IDS) if (map.getLayer(id)) map.moveLayer(id, before);
    return;
  }
  removeAll(map);
  // Bundled land: the always-available offline coast, no network.
  map.addSource(LAND_SRC, {
    type: 'geojson',
    data: landDataUrl(),
    attribution: LAND_ATTRIBUTION,
  });
  // Optional finer online detail from the MapTiler vector tiles.
  if (withTiles) {
    map.addSource(SRC, {
      type: 'vector',
      tiles: [base.url],
      maxzoom: base.maxzoom,
      attribution: base.attribution,
    });
  }
  const before = firstContentLayerId(map);
  for (const spec of specs) map.addLayer(spec, before);
  // With tiles configured, wait for the first one to land and then paint land
  // beneath the fine water fill (see addTilesLand for why we wait).
  if (withTiles) watchTiles(map);
  currentSig = sig;
}
